import axios from 'axios';
import { getAuthHeader, getUserId, isAdmin } from './authService';
import CONFIG, { getApiUrl } from '../utils/config';

// Định nghĩa interface cho loại hoạt động
export interface ActivityType {
  id: number;
  name: string;
  displayName: string;
  description: string | null;
  active: boolean;
}

// Định nghĩa interface cho Tournament
export interface Tournament {
  id: number;
  name: string;
  description: string;
  startDate: number[] | string;
  endDate: number[] | string;
  imageUrl: string;
  teamCount: number;
  status: string;
  createdById: number;
  createdByName: string;
  createdAt: number[] | string;
  updatedAt: number[] | string;
  allowedActivityTypes?: ActivityType[];
  visible: boolean;
}

// Interface cho dữ liệu tạo giải đấu mới
export interface CreateTournamentRequest {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  imageUrl: string;
  createdById?: number;
  activityTypeIds?: number[];
  visible?: boolean;
}

// Interface cho dữ liệu cập nhật giải đấu
export interface UpdateTournamentRequest {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  imageUrl: string;
  status: string;
  activityTypeIds?: number[];
  visible?: boolean;
}

// Chuyển đổi mảng số thành chuỗi ngày
export const formatDateArray = (dateArray: number[]): string => {
  if (!dateArray || dateArray.length < 3) return '';
  
  // Mảng có dạng [năm, tháng, ngày, ...] 
  const [year, month, day] = dateArray;
  // Tạo chuỗi ISO trực tiếp từ mảng để tránh bị lệch ngày
  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

// Hàm lấy tất cả các giải đấu
export const getAllTournaments = async (isForTournamentPage: boolean = true): Promise<Tournament[]> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem danh sách giải đấu');
    }
    
    const response = await axios.get(getApiUrl('/api/tournaments'), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      const tournaments = response.data.data;
      
      // Nếu người dùng không phải admin VÀ đang hiển thị ở trang Tournaments,
      // thì lọc ra các giải đấu có visible = true
      if (!isAdmin() && isForTournamentPage) {
        return tournaments.filter((tournament: Tournament) => tournament.visible !== false);
      }
      
      // Nếu là admin hoặc không phải trang Tournaments, hiển thị tất cả
      return tournaments;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách giải đấu');
    }
  } catch (error: any) {
    console.error('Error fetching tournaments:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm lấy chi tiết một giải đấu
export const getTournamentById = async (id: number): Promise<Tournament> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem chi tiết giải đấu');
    }
    
    const response = await axios.get(getApiUrl(`/api/tournaments/${id}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy thông tin giải đấu');
    }
  } catch (error: any) {
    console.error(`Error fetching tournament #${id}:`, error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm tạo giải đấu mới
export const createTournament = async (data: CreateTournamentRequest): Promise<Tournament> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để tạo giải đấu');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền tạo giải đấu. Chỉ quản trị viên mới có quyền này.');
    }
    
    // Lấy userId của người dùng đang đăng nhập
    const userId = getUserId();
    if (!userId) {
      throw new Error('Không thể xác định thông tin người dùng');
    }
    
    // Thêm createdById vào dữ liệu gửi đi
    const tournamentData = {
      ...data,
      createdById: userId
    };
    
    console.log('Gửi dữ liệu tạo giải đấu:', tournamentData);
    
    const response = await axios.post(getApiUrl('/api/tournaments'), tournamentData, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể tạo giải đấu');
    }
  } catch (error: any) {
    console.error('Error creating tournament:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm cập nhật thông tin giải đấu
export const updateTournament = async (id: number, data: UpdateTournamentRequest): Promise<Tournament> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để cập nhật giải đấu');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền cập nhật giải đấu. Chỉ quản trị viên mới có quyền này.');
    }
    
    const response = await axios.put(getApiUrl(`/api/tournaments/${id}`), data, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể cập nhật giải đấu');
    }
  } catch (error: any) {
    console.error('Error updating tournament:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm xóa giải đấu
export const deleteTournament = async (id: number): Promise<void> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xóa giải đấu');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền xóa giải đấu. Chỉ quản trị viên mới có quyền này.');
    }
    
    const response = await axios.delete(getApiUrl(`/api/tournaments/${id}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result !== 'SUCCESS') {
      throw new Error(response.data.message || 'Không thể xóa giải đấu');
    }
  } catch (error: any) {
    console.error('Error deleting tournament:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm lấy danh sách loại hoạt động
export const getActivityTypes = async (): Promise<ActivityType[]> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để lấy danh sách loại hoạt động');
    }
    
    const response = await axios.get(getApiUrl('/api/activity-types'), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách loại hoạt động');
    }
  } catch (error: any) {
    console.error('Error fetching activity types:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
}; 